if (!customElements.get('hero-slider')) {
  class HeroSlider extends SwiperComponent {
    constructor() {
      super();
      this.lastActiveIndex = null;
      this._onBlockSelect = event => {
        if (this.swiper && event.target.closest('hero-slider') === this) {
          const selectedSlideIndex = +event.target.dataset.index;
          this.swiper.slideTo(selectedSlideIndex, 600);
        }
      };
      this._onKeyDown = event => {
        if (!this.isInViewport()) return;
        if (event.key === 'ArrowRight') {
          this.querySelector('.swiper-button--next')?.click();
        }
        if (event.key === 'ArrowLeft') {
          this.querySelector('.swiper-button--prev')?.click();
        }
      };
    }

    connectedCallback() {
      // Soft-nav reconnect must not stack a second Swiper on a live instance.
      if (this.swiper || this.swiperInitialized) {
        this.destroy();
      }

      this.swiperOptions = this.buildHeroSliderOptions();
      this.section = this.closest('section');

      if (this.section && this.section.classList.contains('shopify-section')) {
        this.observer?.disconnect();
        this.observer = new IntersectionObserver((entries, observer) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              this.init();
              observer.disconnect();
            }
          });
        });
        this.observer.observe(this.section);
      }

      if (Shopify.designMode) {
        window.addEventListener('shopify:section:load', this._onSectionLoad);
        window.addEventListener('shopify:section:select', this._onSectionSelect);

        setTimeout(() => {
          if (!this.swiperInitialized) this.init();
        }, 100);
      } else if (document.readyState === 'complete') {
        // Soft-nav: load already fired; IO inits when section is visible.
        if (!this.section) this.init();
      } else {
        window.addEventListener('load', this._onLoad);
      }

      window.addEventListener('shopify:block:select', this._onBlockSelect);
      document.addEventListener('keydown', this._onKeyDown);
    }

    disconnectedCallback() {
      this.destroy();
    }

    destroy() {
      window.removeEventListener('shopify:block:select', this._onBlockSelect);
      document.removeEventListener('keydown', this._onKeyDown);
      this.slider = null;
      super.destroy();
    }

    buildHeroSliderOptions() {
      const autoplayOptions = {
        delay: this.dataset.autoplayInterval || 5000
      };

      return {
        rewind: true,
        slidesPerView: 1,
        speed: 600,
        followFinger: false,
        navigation: {
          nextEl: '.swiper-button--next',
          prevEl: '.swiper-button--prev'
        },
        pagination: {
          el: '.swiper-pagination',
          clickable: true,
          renderBullet: function (i, className) {
            return `
              <button class="${className}">
                <span>0${i + 1}</span>
                <svg class="square-progress" width="26" height="26">
                  <rect class="square-origin" width="26" height="26" rx="5" ry="5" />
                </svg>
                <svg class="progress" width="1.8rem" height="1.8rem" style="inset-inline-start: ${0 - (i * 2.4 + 3.4)}rem">
                  <circle class="circle-origin" r="0.8rem" cx="0.95rem" cy="0.95rem"></circle>
                </svg>
              </button>
            `;
          }
        },
        autoplay: this.dataset.autoplay === 'true' ? autoplayOptions : false,
        on: {
          init: this.handleSlideChange.bind(this),
          slideChange: this.handleSlideChange.bind(this)
        }
      };
    }

    init() {
      if (this.swiperInitialized) return;
      this.swiperInitialized = true;

      // Use custom Swiper options for HeroSlider
      this.swiper = new Swiper(this, this.swiperOptions);

      // Maintain slider property for backward compatibility
      this.slider = this.swiper;
    }

    handleSlideChange(swiper) {
      this.handleSlideChangeAnimations(swiper);

      /** change header menu color for active slide */
      let headerInner = document.querySelector('.header__inner');
      let heroInners = document.querySelectorAll('.hero__inner');

      if (!headerInner || !heroInners) {
        return;
      }

      // change --transparent-header-menu-text-color value on document style attributes
      document.documentElement.style.setProperty(
        '--transparent-header-menu-text-color',
        heroInners[swiper.activeIndex].dataset.headerMenuTextColor
      );
    }
    /**
     * get direction & set custom transitions
     * with delay for content animation items
     * */
    handleSlideChangeAnimations(swiper) {
      let delay = 300;
      const queryElement = `[data-index="${swiper.activeIndex}"]`;
      const activeSlide = swiper.wrapperEl.querySelector(queryElement);
      if (!activeSlide) return;
      const queryArray = [...(activeSlide.querySelectorAll('.hero__animation') || [])];
      if (!queryArray.length) {
        return;
      }
      const allowMobileAnimation = swiper.wrapperEl.closest('.hero__content')?.dataset.animationMobile === 'true';
      if (!allowMobileAnimation && DeviceDetector.isMobile()) {
        queryArray.forEach(element => {
          element.classList.remove('in-delay');
        });
        return;
      }
      // set all element in delay
      queryArray.forEach(element => {
        element.classList.add('in-delay');
      });

      // get direction and set transitions
      if (this.lastActiveIndex > swiper.activeIndex) {
        delay = 650 + delay;
        swiper.wrapperEl.style.transitionDuration = '1000ms';
        swiper.wrapperEl.style.transitionTimingFunction = 'cubic-bezier(0.45, 0.00, 0.15, 0.95)';
      } else if (this.lastActiveIndex < swiper.activeIndex) {
        delay = 1000 + delay;
        swiper.wrapperEl.style.transitionDuration = '1000ms';
        swiper.wrapperEl.style.transitionTimingFunction = 'cubic-bezier(0.45, 0.00, 0.15, 0.95)';
      }

      // set delay calculated per item
      setTimeout(() => {
        if (this.lastActiveIndex > swiper.activeIndex) {
          delay = 325 + delay;
        } else if (this.lastActiveIndex < swiper.activeIndex) {
          delay = 500 + delay;
        }
        queryArray.forEach((element, index) => {
          if (index === 1) {
            delay = delay + 50;
          } else if (index === 2) {
            delay = delay + 175;
          } else if (index === 3) {
            delay = delay + 300;
          }
          element.classList.remove('in-delay');
        });
      }, delay);

      // set last active global
      this.lastActiveIndex = swiper.activeIndex;
    }

    isInViewport() {
      const rect = this.getBoundingClientRect();
      return (
        rect.top < window.innerHeight && // top part in viewport
        rect.bottom > 0 && // bottom part in viewport
        rect.left < window.innerWidth && // left part in viewport
        rect.right > 0 // right part in viewport
      );
    }
  }
  customElements.define('hero-slider', HeroSlider);
}
